import React from "react";

import { getLocalSession } from "../helpers/authSession";
import SystemIds from "../util/SystemIds";
import { createCtx } from "./context";
import type { IUserProfile } from "./types";

const sixteenHoursInMinutes = 960;

type Layout = {
  /** Determines whether the navigation menu is collapsed when screen size is not mobile or tablet */
  navMenuCollapsed: boolean;
  /** Determines whether navigation menu is collapsed on mobile screen size ONLY*/
  navMobileMenuCollapsed: boolean;
  /** Determines whether the tablet navigation menu is collapsed on tablet screen size ONLY*/
  navTabletMenuCollapsed: boolean;
};

/**
 * Company profile
 *
 */
type CompanyProfile = {
  /** Count of fleets within the company */
  fleetCount: number;
  /** Name of the company */
  companyName: string;
  /** Identifier of the company */
  id: string;
  /** preference setting */
  isPreferredCompany: boolean;
  /** primary company user belongs to */
  isPrimaryCompany: boolean;
  /** vehicle count under the company */
  vehicleCount: number;
};

/**
 * Application wide storage
 * : in-memory SPA context
 */
type AppStore = {
  /** */
  active: boolean;
  /** Determines whether AI Assistant is open */
  isAIAssistantOpen: boolean;
  /** Auth token JWT */
  token: string;
  /** Active user profile */
  userProfile?: IUserProfile;
  /** function to set properties of the app storage */
  setAppStore?: (appStore: AppStore) => void;
  /** Determines whether the user has been authenticated */
  isAuthenticated: boolean;
  /** Determines the expiry time in minutes */
  expiryTimeInMinutes: number;
  /** Layout components */
  layout: Layout;
};

/**
 * App local storage which will expire with a given time to live
 *
 * @interface ExpiryStore
 */
interface ExpiryStore {
  /** the local storage key */
  value: string;
  /** The active users profile */
  profile?: IUserProfile;
  /** The time to live duration in ms to remain in local storage */
  expiry?: number;
}

const [useAppContext, AppContextProvider] = createCtx<AppStore | undefined>();
const AppActionsContext = React.createContext<(appStore: Partial<AppStore>) => void>(() => {});
const useAppActionsContext = (): ((appStore: Partial<AppStore>) => void) => {
  const context = React.useContext(AppActionsContext);
  if (context === undefined) {
    throw new Error(`Context must be called within Provider`);
  }
  return context;
};
type Props = { children: React.ReactNode };

const AppProvider = ({ children }: Props): React.ReactElement => {
  const localSession = getLocalSession();
  const token = localSession?.token ?? "";
  const userProfile = localSession?.userProfile ?? undefined;
  const isAuthenticated = token.trim() !== "" && userProfile?.companyId !== SystemIds.Empty && !!userProfile?.username;

  const [appStore, setAppStore] = React.useState<AppStore>({
    active: true,
    isAIAssistantOpen: false,
    token,
    isAuthenticated,
    userProfile,
    expiryTimeInMinutes: sixteenHoursInMinutes,
    layout: {
      navMenuCollapsed: false,
      navMobileMenuCollapsed: true,
      navTabletMenuCollapsed: true,
    },
  });

  return (
    <AppContextProvider value={appStore}>
      <AppActionsContext.Provider
        value={(store: Partial<AppStore>) =>
          setAppStore((prev: $TSFixMe) => ({
            ...prev,
            ...store,
          }))
        }
      >
        {children}
      </AppActionsContext.Provider>
    </AppContextProvider>
  );
};

export type { AppStore, CompanyProfile, ExpiryStore, IUserProfile };
export { AppContextProvider, AppProvider, useAppActionsContext, useAppContext };
